import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { track, ANALYTICS_EVENTS } from '../../utils/productAnalytics';
import { useSearchParams } from 'react-router-dom';
import {
  GenerationInputMode,
  GenerationInputModeTabs,
  ProductGenerationPanel,
  ProductImageSelectionModal,
  ProductInfoCopyModal,
  ProductSelectionData,
  ProductSelectionModal,
  SelectedProductImage,
} from '../image-generation';
import Loading from '../common/Loading';
import { TaskySegmentedControl, TaskySelect, useConfirm, TaskyPageContainer } from '../tasky';
import { useToast } from '../../contexts/ToastContext';
import BetaAlert from '../common/BetaAlert';
import GeneratingVideoJobsSection from './GeneratingVideoJobsSection';
import VideoJobDetailModal from './VideoJobDetailModal';
import { resubmitVideoJob, VideoJobResubmitOverrides } from './resubmitVideoJob';
import VideoStyleSelectModal, { VideoTemplate, SelectedVideoStyle } from './VideoStyleSelectModal';
import VideoModelSelectModal from './VideoModelSelectModal';
import {
  checkPromptLength,
  getVideoModelSpec,
  getDurationOptions,
  getResolutionOptions,
  getAspectRatioOptions,
  reconcileSettingsForModel,
} from './videoModelCatalog';
import videoTemplateService from '../../services/api/videoTemplateService';
import videoGenerationService, {
  GeneratedVideo,
  VideoGenerationJob,
  VideoModelType,
  VideoDurationSeconds,
  VideoGenerationSource,
} from '../../services/api/videoGenerationService';
import videoPromptService, {
  VideoPromptFavorite,
  PromptHistory,
} from '../../services/api/videoPromptService';
import { buildProductGenerationPrompt } from '../../utils/productGenerationPrompt';
import TaapBannerHandoff from '../taap/TaapBannerHandoff';
import { useSetAdmanPageContext } from '../adman/useAdmanPageContext';
import {
  FolderOpen,
  FileText,
  X,
  Download,
  Video,
  Upload,
  User,
  Users,
  Play,
  Loader2,
  Heart,
  History,
  Palette,
  Volume2,
  VolumeX,
  HelpCircle,
} from 'lucide-react';

interface VideoGenerationTabProps {
  onError?: (error: string) => void;
  refImageS3Key?: string;
  refImageUrl?: string;
  refPrompt?: string;
  generationSource?: VideoGenerationSource;
  preferredStyle?: string;
  imageAspectRatio?: '1:1' | '16:9' | '9:16';
}

// VideoGenerationJobController::generateAsync の reference_images max:5 と揃える
const MAX_REFERENCE_IMAGES = 5;

const VideoGenerationTab: React.FC<VideoGenerationTabProps> = ({ onError, refImageS3Key, refImageUrl, refPrompt, generationSource, preferredStyle, imageAspectRatio }) => {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-linked job detail modal (from /home 最近の作業 → このプロジェクトを開く)
  const [deepLinkedJobId, setDeepLinkedJobId] = useState<string | null>(null);

  useEffect(() => {
    const jobId = searchParams.get('job');
    if (jobId) {
      setDeepLinkedJobId(jobId);
      const next = new URLSearchParams(searchParams);
      next.delete('job');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Form state
  const [prompt, setPrompt] = useState('');
  const [inputMode, setInputMode] = useState<GenerationInputMode>('product');
  const [selectedProductData, setSelectedProductData] = useState<ProductSelectionData | null>(null);
  // 広告自動化マンに「今この商品を見ている」と伝える（F20-a）。既存ロジックには触れない。
  useSetAdmanPageContext(
    selectedProductData ? { product_ids: [selectedProductData.product.id] } : null
  );
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [videoModel, setVideoModel] = useState<VideoModelType>('seedance-2-mini');
  // Seedance 2.0 supports 16:9 and 9:16; duration defaults to 8s (shared range with Gemini)
  const [aspectRatio, setAspectRatio] = useState<'1:1' | '16:9' | '9:16'>(imageAspectRatio || '16:9');
  const [durationSeconds, setDurationSeconds] = useState<VideoDurationSeconds>(8);
  const [resolution, setResolution] = useState<'720p' | '1080p'>('720p');
  const [generateAudio, setGenerateAudio] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [jobsRefreshKey, setJobsRefreshKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string | null>(null);
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [showModelModal, setShowModelModal] = useState(false);

  // Reference images
  const [showImageSelectionModal, setShowImageSelectionModal] = useState(false);
  const [showProductInfoModal, setShowProductInfoModal] = useState(false);
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
  const [referenceImages, setReferenceImages] = useState<SelectedProductImage[]>([]);

  // Uploaded reference images
  const [uploadedReferenceImages, setUploadedReferenceImages] = useState<Array<{
    id: string;
    file: File;
    preview: string;
    base64: string;
    category: 'brand_logo' | 'product_image' | 'material';
  }>>([]);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  // History state
  const [historyView, setHistoryView] = useState<'my' | 'organization'>('my');
  const [videoHistory, setVideoHistory] = useState<GeneratedVideo[]>([]);
  const [organizationVideos, setOrganizationVideos] = useState<GeneratedVideo[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [orgCurrentPage, setOrgCurrentPage] = useState(1);
  const [orgTotalPages, setOrgTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isOrgLoading, setIsOrgLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [orgHasMore, setOrgHasMore] = useState(true);

  // Selected video for detail view
  const [selectedVideo, setSelectedVideo] = useState<GeneratedVideo | null>(null);

  // Phase 3: Prompt favorites and history
  const [showPromptPanel, setShowPromptPanel] = useState<'favorites' | 'history' | null>(null);
  const [favorites, setFavorites] = useState<VideoPromptFavorite[]>([]);
  const [promptHistory, setPromptHistory] = useState<PromptHistory[]>([]);

  // Phase 3: Compare mode

  // Phase 3: Batch generation

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Initialize from banner reference (when navigating from image generation)
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (refImageS3Key) {
      setInputMode('freetext');
      // Add the reference image from banner as a product image reference
      setReferenceImages([{
        id: -1, // Temporary ID for banner reference
        url: refImageUrl || '',
        s3Key: refImageS3Key,
        productId: 0,
        productName: '',
        fileName: 'banner-reference.png',
        category: 'material',
      }]);
    }

    if (refPrompt) {
      setPrompt(refPrompt);
      setInputMode('freetext');
    }
    // When no refPrompt, the template prompt will be auto-set via preferredStyle matching
  }, [refImageS3Key, refPrompt]);

  // Fetch video templates from API
  const [adTemplates, setAdTemplates] = useState<VideoTemplate[]>([]);
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const items = await videoTemplateService.getTemplates();
        const mapped: VideoTemplate[] = items.map(item => ({
          id: String(item.id),
          label: item.name,
          icon: item.icon || '🎬',
          description: item.description || '',
          gradient: item.gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          category: item.category,
          prompt: item.prompt_template,
          sampleVideoUrl: item.sample_video_url,
          sampleVideoPosterUrl: item.sample_poster_url,
          thumbnailUrl: item.thumbnail_url || null,
        }));
        setAdTemplates(mapped);
      } catch (err) {
        console.error('Failed to fetch video templates:', err);
      }
    };
    fetchTemplates();
  }, []);

  // Auto-select preferred style when templates are loaded and preferredStyle is specified
  useEffect(() => {
    if (!preferredStyle || adTemplates.length === 0 || !initializedRef.current) return;
    // Already has a selected template, skip
    if (selectedTemplateId) return;

    // Find template matching the preferred style name
    const matchingTemplate = adTemplates.find(t =>
      t.label.includes(preferredStyle) || t.label === preferredStyle
    );

    if (matchingTemplate) {
      handleStyleSelect({
        id: matchingTemplate.id,
        label: matchingTemplate.label,
        prompt: matchingTemplate.prompt,
        category: matchingTemplate.category,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adTemplates, preferredStyle, selectedTemplateId]);

  const handleStyleSelect = (selectedStyle: SelectedVideoStyle | null) => {
    if (selectedStyle) {
      setSelectedTemplateId(selectedStyle.id);
      setSelectedTemplateName(selectedStyle.label);
      if (prompt.trim()) {
        // Merge: template + user's existing prompt
        setPrompt(`${selectedStyle.prompt}\n\n${prompt}`);
      } else {
        setPrompt(selectedStyle.prompt);
      }
    } else {
      setSelectedTemplateId(null);
      setSelectedTemplateName(null);
    }
  };

  // モデルごとの制約は videoModelCatalog が唯一の真実。
  // 以前はここに尺・解像度・アスペクト比・音声の分岐が個別に並んでおり、
  // モデルを追加するたびに全部を直す必要があった。
  const modelSpec = getVideoModelSpec(videoModel);
  const aspectRatioOptions = getAspectRatioOptions(videoModel);
  const durationOptions = getDurationOptions(videoModel);
  const resolutionOptions = getResolutionOptions(videoModel);
  const supportsSquare = modelSpec.supportsSquare;
  const isFalModel = videoModel === 'fal-h3-max' || videoModel === 'fal-h3-max-turbo';
  const isFalTurbo = videoModel === 'fal-h3-max-turbo';
  const tooManyFalReferences = isFalTurbo && referenceImages.length + uploadedReferenceImages.length > 1;

  useEffect(() => {
    if (!supportsSquare && aspectRatio === '1:1') {
      setAspectRatio('16:9');
    }
  }, [aspectRatio, supportsSquare]);

  // 動画スタイル（テンプレート）を選ぶと AI によるプロンプト最適化をスキップする。
  // スキップ時は長文がそのままプロバイダへ渡るので、文字数上限が直接効いてくる。
  const skipsPromptOptimization = !!selectedTemplateId || isFalModel;

  // 実際に送信される文字列。カウンタと送信で別々に組み立てると、
  // 「UIは大丈夫と言ったのにAPIが弾く」ずれが生まれる。
  const effectivePrompt = useMemo(
    () =>
      inputMode === 'product' && selectedProductData
        ? [
            '選択した商品の広告動画を生成してください。',
            buildProductGenerationPrompt(selectedProductData, additionalInstructions),
            selectedTemplateId && prompt.trim() ? `動画スタイル指示:\n${prompt.trim()}` : '',
          ]
            .filter(Boolean)
            .join('\n\n')
        : prompt.trim(),
    [inputMode, selectedProductData, additionalInstructions, selectedTemplateId, prompt]
  );

  // 上限判定はカタログ側の純関数に任せる（モデル別の制約の置き場所を一本化するため）
  const promptLength = useMemo(
    () =>
      checkPromptLength(videoModel, effectivePrompt, {
        skipsOptimization: skipsPromptOptimization,
      }),
    [videoModel, effectivePrompt, skipsPromptOptimization]
  );

  // MiniMax H3 は音声がネイティブ生成で、ON/OFF を指定できない（常に音声つき）
  const audioAlwaysOn = modelSpec.audio === 'always';
  const audioEnabled = audioAlwaysOn || generateAudio;

  // モデルを切り替えたら、新モデルで無効になる設定だけを補正する。
  const handleModelSelect = useCallback(
    (newModel: VideoModelType) => {
      const next = reconcileSettingsForModel(newModel, {
        durationSeconds,
        resolution,
        aspectRatio,
      });
      setVideoModel(newModel);
      setDurationSeconds(next.durationSeconds);
      setResolution(next.resolution);
      setAspectRatio(next.aspectRatio);
    },
    [durationSeconds, resolution, aspectRatio]
  );

  // Fetch video history
  const fetchVideoHistory = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await videoGenerationService.getVideoHistory(currentPage, generationSource);
      if (response.success) {
        const newVideos = response.data.data;
        if (currentPage === 1) {
          setVideoHistory(newVideos);
        } else {
          setVideoHistory(prev => [...prev, ...newVideos]);
        }
        setTotalPages(response.data.last_page);
        setHasMore(currentPage < response.data.last_page);
      }
    } catch (err: any) {
      console.error('Failed to fetch video history:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, generationSource]);

  const fetchOrganizationVideoHistory = useCallback(async () => {
    try {
      setIsOrgLoading(true);
      const response = await videoGenerationService.getOrganizationVideoHistory(orgCurrentPage, generationSource);
      if (response.success) {
        const newVideos = response.data.data;
        if (orgCurrentPage === 1) {
          setOrganizationVideos(newVideos);
        } else {
          setOrganizationVideos(prev => [...prev, ...newVideos]);
        }
        setOrgTotalPages(response.data.last_page);
        setOrgHasMore(orgCurrentPage < response.data.last_page);
      }
    } catch (err: any) {
      console.error('Failed to fetch organization video history:', err);
    } finally {
      setIsOrgLoading(false);
    }
  }, [orgCurrentPage, generationSource]);

  // Phase 3: Load favorites
  const loadFavorites = useCallback(async () => {
    try {
      const data = await videoPromptService.getFavorites();
      setFavorites(data);
    } catch (err: any) {
      console.error('Failed to load favorites:', err);
    }
  }, []);

  // Phase 3: Load history
  const loadHistory = useCallback(async () => {
    try {
      const data = await videoPromptService.getHistory();
      setPromptHistory(data);
    } catch (err: any) {
      console.error('Failed to load history:', err);
    }
  }, []);

  useEffect(() => {
    if (historyView === 'my') {
      fetchVideoHistory();
    }
  }, [currentPage, historyView, fetchVideoHistory]);

  useEffect(() => {
    if (historyView === 'organization') {
      fetchOrganizationVideoHistory();
    }
  }, [orgCurrentPage, historyView, fetchOrganizationVideoHistory]);

  const handleJobCompleted = () => {
    setCurrentPage(1);
    setVideoHistory([]);
    fetchVideoHistory();
  };

  // ?job=<uuid> で直接開いた失敗ジョブの作り直し。一覧の更新経路は新規生成と同じ。
  const handleResubmitJob = async (
    job: VideoGenerationJob,
    overrides: VideoJobResubmitOverrides
  ) => {
    await resubmitVideoJob(job, overrides);
    setJobsRefreshKey((key) => key + 1);
  };

  const handleGenerateVideo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (tooManyFalReferences) {
      setError('H3 Max Turboの参考画像は開始画像1枚までです。複数画像にはH3 Maxを選択してください。');
      return;
    }
    setError(null);

    if (inputMode === 'product' && !selectedProductData) {
      setError('商品を選択してください');
      return;
    }
    if (inputMode === 'freetext' && !prompt.trim()) {
      setError('プロンプトを入力してください');
      return;
    }
    if (promptLength.blocksGeneration) {
      setError(
        `${modelSpec.label}に渡せるプロンプトは${promptLength.max?.toLocaleString()}文字までです（現在${promptLength.count.toLocaleString()}文字）。動画スタイルを外すか、プロンプトを短くしてください。`
      );
      return;
    }

    setIsGenerating(true);

    try {
      // Prepare reference images
      const allRefImages: Array<{
        type: 'brand_logo' | 'product_image' | 'material';
        s3_key?: string;
        image_data?: string;
      }> = [];

      // Add product library images
      referenceImages.forEach((img) => {
        allRefImages.push({
          type: img.category,
          s3_key: img.s3Key,
        });
      });

      // Add uploaded images
      uploadedReferenceImages.forEach((img) => {
        allRefImages.push({
          type: img.category,
          image_data: img.base64,
        });
      });

      const response = await videoGenerationService.generateAsync(
        effectivePrompt,
        aspectRatio,
        allRefImages.length > 0 ? allRefImages : undefined,
        durationSeconds,
        resolution,
        videoModel,
        generationSource,
        skipsPromptOptimization,
        audioEnabled
      );

      if (response.success) {
        // Reset form
        setJobsRefreshKey((key) => key + 1);
        setPrompt('');
        setSelectedProductData(null);
        setAdditionalInstructions('');
        setReferenceImages([]);
        setSelectedTemplateId(null);
        setSelectedTemplateName(null);
        // Clean up uploaded images
        uploadedReferenceImages.forEach(img => URL.revokeObjectURL(img.preview));
        setUploadedReferenceImages([]);
      } else {
        setError(response.message || '動画生成ジョブの開始に失敗しました');
      }
    } catch (err: any) {
      console.error('Error starting video generation:', err);
      setError(err.response?.data?.message || '動画生成ジョブの開始中にエラーが発生しました');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReferenceImagesSelect = (selected: SelectedProductImage[]) => {
    setReferenceImages(selected);
  };

  const handleRemoveReferenceImage = (imageId: number) => {
    setReferenceImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleCopyToPrompt = (text: string) => {
    setPrompt(prev => prev ? `${prev}\n\n${text}` : text);
  };

  const handleProductSelected = (data: ProductSelectionData) => {
    setSelectedProductData(data);
    setShowProductSelectionModal(false);
    // 商品を変えたら前の商品の画像は残さない（画像なしの商品でも空にする）。
    // 動画 API の参考画像は 5 枚までなので、ここで上限に収める。
    setReferenceImages(data.selectedImages.slice(0, MAX_REFERENCE_IMAGES).map((image) => ({
      id: image.id,
      url: image.url,
      s3Key: image.file_path,
      productId: image.product_id,
      productName: data.product.name,
      fileName: image.file_name,
      category: image.category,
    })));
  };

  const handleReferenceImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newImages: typeof uploadedReferenceImages = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!file.type.startsWith('image/')) {
        continue;
      }

      if (file.size > 10 * 1024 * 1024) {
        showToast(`${file.name} は10MBを超えているためスキップされました`, 'error');
        continue;
      }

      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const preview = URL.createObjectURL(file);

      newImages.push({
        id: `upload-${Date.now()}-${i}`,
        file,
        preview,
        base64,
        category: 'material',
      });
    }

    setUploadedReferenceImages(prev => [...prev, ...newImages]);

    if (uploadInputRef.current) {
      uploadInputRef.current.value = '';
    }
  };

  const handleRemoveUploadedImage = (imageId: string) => {
    setUploadedReferenceImages(prev => {
      const imageToRemove = prev.find(img => img.id === imageId);
      if (imageToRemove) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
      return prev.filter(img => img.id !== imageId);
    });
  };

  const handleUploadedImageCategoryChange = (imageId: string, category: 'brand_logo' | 'product_image' | 'material') => {
    setUploadedReferenceImages(prev =>
      prev.map(img =>
        img.id === imageId ? { ...img, category } : img
      )
    );
  };

  const handleDeleteVideo = async (id: number) => {
    if (!(await confirm({ title: 'この動画を削除しますか？', tone: 'destructive', confirmText: '削除する' }))) {
      return;
    }

    try {
      const response = await videoGenerationService.deleteVideo(id);
      if (response.success) {
        setVideoHistory(prev => prev.filter(v => v.id !== id));
        setOrganizationVideos(prev => prev.filter(v => v.id !== id));
        if (selectedVideo?.id === id) {
          setSelectedVideo(null);
        }
      }
    } catch (err: any) {
      console.error('Error deleting video:', err);
      showToast('動画の削除に失敗しました', 'error');
    }
  };

  const handleDownloadVideo = (url: string, prompt: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `video-${prompt.substring(0, 30)}.mp4`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    track(ANALYTICS_EVENTS.DOWNLOAD_COMPLETED, { type: 'movie' });
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins > 0 ? `${mins}分${secs}秒` : `${secs}秒`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const currentContent = historyView === 'my' ? videoHistory : organizationVideos;
  const currentLoading = historyView === 'my' ? isLoading : isOrgLoading;
  const currentHasMore = historyView === 'my' ? hasMore : orgHasMore;

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container && container.scrollTop + container.clientHeight >= container.scrollHeight - 800) {
      if (historyView === 'my') {
        if (hasMore && !isLoading) {
          setCurrentPage(prev => prev + 1);
        }
      } else {
        if (orgHasMore && !isOrgLoading) {
          setOrgCurrentPage(prev => prev + 1);
        }
      }
    }
  }, [hasMore, isLoading, orgHasMore, isOrgLoading, historyView]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  return (
    <TaskyPageContainer ref={scrollContainerRef}>
      <BetaAlert featureName="動画生成" />
      {/* Generation Form */}
      <div className="bg-gray-50 rounded-xl p-6 mb-6">
        <TaapBannerHandoff onApply={(data) => {
          setSelectedProductData(data);
          setInputMode('product');
          setAdditionalInstructions('');
          setReferenceImages([]);
          setUploadedReferenceImages([]);
          setError(null);
        }} />
        <div className="space-y-6">
          {/* Style Selection Button */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              動画スタイル
              <div className="relative inline-flex group">
                <button
                  type="button"
                  className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="スタイルについて"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
                <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 absolute left-full ml-2 top-0 w-72 bg-gray-900 text-white text-xs rounded-lg shadow-xl p-4 leading-relaxed z-50 pointer-events-none">
                  <div className="font-bold text-sm mb-2">スタイルとは？</div>
                  <p className="mb-3">
                    スタイルとは、広告クリエイティブ制作におけるレイアウト構造・情報配置・表現ルールの方向性を示すフォーマットです。
                  </p>
                  <p className="mb-3">
                    ここで定義されるのは、どの要素をどの順序で見せるかという設計指針であり、コピー内容や商品、具体的なビジュアルは含みません。
                  </p>
                  <p>
                    そのため、このスタイルを使用して生成されたクリエイティブは参考スタイルの方向性を踏まえたものになりますが、必ずしも同一のデザインや構成が再現されるわけではありません。
                  </p>
                  <div className="absolute left-[-6px] top-2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-gray-900" />
                </div>
              </div>
            </label>
            <button
              type="button"
              onClick={() => setShowStyleModal(true)}
              disabled={isGenerating}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all ${
                selectedTemplateId
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50/50'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <div className="flex items-center gap-3">
                <Palette className={`w-5 h-5 ${selectedTemplateId ? 'text-purple-600' : 'text-gray-400'}`} />
                <span className={`text-sm font-medium ${selectedTemplateId ? 'text-purple-700' : 'text-gray-500'}`}>
                  {selectedTemplateName || 'スタイルを選ぶ'}
                </span>
              </div>
              <span className="text-xs text-gray-400">
                {selectedTemplateId ? '変更' : `${adTemplates.length}種類のテンプレート`}
              </span>
            </button>
            {selectedTemplateId && (
              <button
                type="button"
                onClick={() => {
                  setSelectedTemplateId(null);
                  setSelectedTemplateName(null);
                }}
                className="mt-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                スタイルをクリア
              </button>
            )}
          </div>

          <GenerationInputModeTabs
            value={inputMode}
            onChange={setInputMode}
            disabled={isGenerating}
          />

          {inputMode === 'product' ? (
            <ProductGenerationPanel
              selectedProductData={selectedProductData}
              additionalInstructions={additionalInstructions}
              onAdditionalInstructionsChange={setAdditionalInstructions}
              onSelectProduct={() => setShowProductSelectionModal(true)}
              disabled={isGenerating}
            />
          ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              動画の内容
            </label>
            <textarea
              placeholder="例: 商品が回転しながら登場し、その特徴がテキストで表示される広告動画"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="w-full min-h-[100px] p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              disabled={isGenerating}
            />

            {/* Prompt Actions */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <button
                type="button"
                onClick={async () => {
                  if (prompt.trim()) {
                    try {
                      await videoPromptService.saveFavorite(prompt);
                      loadFavorites();
                    } catch (err) {
                      console.error('Failed to save favorite:', err);
                    }
                  }
                }}
                className="text-xs text-gray-500 hover:text-red-500 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                disabled={!prompt.trim()}
              >
                <Heart className="w-3 h-3" /> お気に入り保存
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPromptPanel(showPromptPanel === 'favorites' ? null : 'favorites');
                  if (showPromptPanel !== 'favorites') loadFavorites();
                }}
                className={`text-xs flex items-center gap-1 transition-colors ${
                  showPromptPanel === 'favorites'
                    ? 'text-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Heart className="w-3 h-3" /> お気に入り
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPromptPanel(showPromptPanel === 'history' ? null : 'history');
                  if (showPromptPanel !== 'history') loadHistory();
                }}
                className={`text-xs flex items-center gap-1 transition-colors ${
                  showPromptPanel === 'history'
                    ? 'text-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <History className="w-3 h-3" /> 履歴
              </button>
            </div>

            {/* Favorites/History Panel */}
            {showPromptPanel && (
              <div className="mt-2 border rounded-lg p-3 max-h-40 overflow-y-auto bg-gray-50">
                {showPromptPanel === 'favorites' && (
                  favorites.length > 0 ? (
                    favorites.map(fav => (
                      <div
                        key={fav.id}
                        className="flex items-center justify-between py-1.5 border-b last:border-0"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setPrompt(fav.prompt);
                            setShowPromptPanel(null);
                          }}
                          className="text-sm text-gray-700 hover:text-purple-600 text-left truncate flex-1 transition-colors"
                        >
                          {fav.label || fav.prompt.substring(0, 60) + '...'}
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await videoPromptService.deleteFavorite(fav.id);
                              loadFavorites();
                            } catch (err) {
                              console.error('Failed to delete favorite:', err);
                            }
                          }}
                          className="text-xs text-gray-400 hover:text-red-500 ml-2 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-400">お気に入りはまだありません</p>
                  )
                )}
                {showPromptPanel === 'history' && (
                  promptHistory.length > 0 ? (
                    promptHistory.map(h => (
                      <button
                        key={h.id}
                        type="button"
                        onClick={() => {
                          setPrompt(h.prompt);
                          setShowPromptPanel(null);
                        }}
                        className="block w-full text-left py-1.5 border-b last:border-0 text-sm text-gray-700 hover:text-purple-600 truncate transition-colors"
                      >
                        {h.prompt.substring(0, 80)}...
                        <span className="text-xs text-gray-400 ml-2">
                          {new Date(h.created_at).toLocaleDateString('ja-JP')}
                        </span>
                      </button>
                    ))
                  ) : (
                    <p className="text-xs text-gray-400">履歴はまだありません</p>
                  )
                )}
              </div>
            )}
          </div>
          )}

          {/* Reference Images Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              参考画像（任意）
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowImageSelectionModal(true)}
                disabled={isGenerating}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FolderOpen className="w-4 h-4" />
                画像ライブラリ
              </button>
              {inputMode === 'freetext' && (
                <button
                  type="button"
                  onClick={() => setShowProductInfoModal(true)}
                  disabled={isGenerating}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileText className="w-4 h-4" />
                  商品情報をコピー
                </button>
              )}
              <button
                type="button"
                onClick={() => uploadInputRef.current?.click()}
                disabled={isGenerating}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className="w-4 h-4" />
                アップロード
              </button>
              <input
                ref={uploadInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleReferenceImageUpload}
                className="hidden"
              />
              {(referenceImages.length > 0 || uploadedReferenceImages.length > 0) && (
                <span className="text-sm text-gray-600">
                  {referenceImages.length + uploadedReferenceImages.length}枚選択中
                </span>
              )}
            </div>

            {/* Reference Images Preview */}
            {(referenceImages.length > 0 || uploadedReferenceImages.length > 0) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {referenceImages.map((img) => (
                  <div
                    key={img.id}
                    className="relative group w-20 h-20 rounded-lg overflow-hidden border-2 border-purple-500"
                  >
                    <img
                      src={img.url}
                      alt={img.fileName}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveReferenceImage(img.id)}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs px-1 py-0.5 truncate">
                      {img.category === 'brand_logo' && 'ロゴ'}
                      {img.category === 'product_image' && '商品'}
                      {img.category === 'material' && '素材'}
                    </div>
                  </div>
                ))}
                {uploadedReferenceImages.map((img) => (
                  <div
                    key={img.id}
                    className="relative group w-20 h-20 rounded-lg overflow-hidden border-2 border-green-500"
                  >
                    <img
                      src={img.preview}
                      alt={img.file.name}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveUploadedImage(img.id)}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <select
                      value={img.category}
                      onChange={(e) => handleUploadedImageCategoryChange(img.id, e.target.value as 'brand_logo' | 'product_image' | 'material')}
                      onClick={(e) => e.stopPropagation()}
                      className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-0.5 py-0.5 border-none outline-none cursor-pointer"
                    >
                      <option value="brand_logo">ロゴ</option>
                      <option value="product_image">商品</option>
                      <option value="material">素材</option>
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>

          {isFalTurbo && (
            <div className={`rounded-md border p-3 ${tooManyFalReferences ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'}`}>
              <p className="text-sm text-gray-800">
                Turboの参考画像は開始フレームとして使う1枚です。選択した縦横比に合わせて余白を付けます。
                {tooManyFalReferences && '画像を1枚に減らすか、複数画像に対応するH3 Maxを選択してください。'}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">動画生成モデル</label>
              <button
                type="button"
                onClick={() => setShowModelModal(true)}
                aria-label={`動画生成モデルを選択: ${modelSpec.label}`}
                disabled={isGenerating}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white p-3 text-left transition-colors hover:border-violet-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm text-gray-800">{modelSpec.label}</span>
                  <span className="block truncate text-xs text-gray-500">{modelSpec.tagline}</span>
                </span>
                <span className="shrink-0 text-xs font-medium text-violet-600">変更</span>
              </button>
            </div>

            <div>
              <label htmlFor="video-ad-aspectRatio" className="block text-sm font-medium text-gray-700 mb-2">アスペクト比</label>
              <TaskySelect
                id="video-ad-aspectRatio"
                value={aspectRatio}
                onChange={(e) => setAspectRatio(e.target.value as '1:1' | '16:9' | '9:16')}
                disabled={isGenerating}
              >
                {aspectRatioOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} - {option.description}
                  </option>
                ))}
              </TaskySelect>
              {!supportsSquare && (
                <p className="mt-1 text-xs text-gray-500">{modelSpec.label}では1:1を選べません。</p>
              )}
            </div>

            <div>
              <label htmlFor="video-ad-durationSeconds" className="block text-sm font-medium text-gray-700 mb-2">動画の長さ</label>
              <TaskySelect
                id="video-ad-durationSeconds"
                value={durationSeconds}
                onChange={(e) => setDurationSeconds(Number(e.target.value) as 5 | 6 | 8 | 12 | 15)}
                disabled={isGenerating}
              >
                {durationOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} - {option.description}
                  </option>
                ))}
              </TaskySelect>
            </div>

            <div>
              <label htmlFor="video-ad-resolution" className="block text-sm font-medium text-gray-700 mb-2">解像度</label>
              <TaskySelect
                id="video-ad-resolution"
                value={resolution}
                onChange={(e) => setResolution(e.target.value as '720p' | '1080p')}
                disabled={isGenerating}
              >
                {resolutionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} - {option.description}
                  </option>
                ))}
              </TaskySelect>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">音声</label>
              <button
                type="button"
                onClick={() => setGenerateAudio(!generateAudio)}
                disabled={isGenerating || audioAlwaysOn}
                title={audioAlwaysOn ? 'このモデルは常に音声つきで生成されます' : undefined}
                className={`flex items-center gap-2 px-4 py-3 rounded-md border transition-colors w-full ${
                  audioEnabled
                    ? 'bg-purple-50 border-purple-300 text-purple-700'
                    : 'bg-gray-50 border-gray-300 text-gray-500'
                } ${isGenerating || audioAlwaysOn ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-100'}`}
              >
                {audioEnabled ? (
                  <Volume2 className="w-4 h-4" />
                ) : (
                  <VolumeX className="w-4 h-4" />
                )}
                <span className="text-sm font-medium">
                  {audioAlwaysOn ? '音声あり（自動）' : generateAudio ? '音声あり' : '音声なし'}
                </span>
              </button>
            </div>

            {promptLength.isOver && (
              <div
                className={`rounded-md border p-3 ${
                  promptLength.blocksGeneration
                    ? 'border-red-200 bg-red-50'
                    : 'border-amber-200 bg-amber-50'
                }`}
              >
                <p
                  className={`text-sm ${
                    promptLength.blocksGeneration ? 'text-red-800' : 'text-amber-800'
                  }`}
                >
                  {modelSpec.label}に渡せるプロンプトは{promptLength.max?.toLocaleString()}文字までです（現在
                  {promptLength.count.toLocaleString()}文字）。
                  {promptLength.blocksGeneration
                    ? '動画スタイルを選んでいる間はAIによる要約が行われないため、このままでは生成できません。スタイルを外すか、プロンプトを短くしてください。'
                    : '送信時にAIが要約して収めます。要約できなかった場合は生成が止まるので、重要な指示は前半に書いてください。'}
                </p>
              </div>
            )}

            <div className="flex items-end gap-2">
              <button
                onClick={handleGenerateVideo}
                disabled={
                  isGenerating ||
                  promptLength.blocksGeneration ||
                  tooManyFalReferences ||
                  (inputMode === 'product' ? !selectedProductData : !prompt.trim())
                }
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    生成開始中...
                  </>
                ) : (
                  <>
                    <Video className="w-4 h-4" />
                    動画を生成
                  </>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-sm text-blue-800">
              <strong>注意:</strong> 動画生成には数分かかる場合があります。生成中はこのページを離れても処理は継続されます。
            </p>
          </div>
        </div>
      </div>

      {/* Generating Jobs Section */}
      <GeneratingVideoJobsSection onJobCompleted={handleJobCompleted} generationSource={generationSource} refreshKey={jobsRefreshKey} />

      {/* History View Toggle and Compare Mode */}
      <div className="flex items-center space-x-4 mb-4 flex-wrap gap-2">
        <TaskySegmentedControl<'my' | 'organization'>
          aria-label="動画の表示範囲"
          value={historyView}
          onChange={(next) => {
            if (next === 'my') {
              setHistoryView('my');
              setCurrentPage(1);
              setVideoHistory([]);
            } else {
              setHistoryView('organization');
              setOrgCurrentPage(1);
              setOrganizationVideos([]);
            }
          }}
          options={[
            {
              value: 'my',
              label: (
                <span className="inline-flex items-center gap-1.5">
                  <User className="w-4 h-4" />
                  自分の動画
                </span>
              ),
            },
            {
              value: 'organization',
              label: (
                <span className="inline-flex items-center gap-1.5">
                  <Users className="w-4 h-4" />
                  組織の動画
                </span>
              ),
            },
          ]}
        />
      </div>

      {/* Video Grid */}
      {currentContent.length === 0 ? (
        <p className="text-center text-gray-500 py-8">まだ動画が生成されていません</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {currentContent.map((video) => {
            return (
              <div
                key={video.id}
                className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200 group cursor-pointer"
                onClick={() => {
                  setSelectedVideo(video);
                }}
              >
                <div className="relative bg-black aspect-video">
                  <video
                    src={video.url}
                    className="w-full h-full object-contain"
                    preload="metadata"
                    onClick={(e) => {
                      setSelectedVideo(video);
                      e.stopPropagation();
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-30">
                    <button
                      onClick={(e) => {
                        setSelectedVideo(video);
                        e.stopPropagation();
                      }}
                      className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center"
                    >
                      <Play className="w-8 h-8 text-gray-800 ml-1" />
                    </button>
                  </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadVideo(video.url, video.prompt);
                    }}
                    className="h-8 w-8 p-0 bg-white rounded-md shadow-md flex items-center justify-center hover:bg-gray-100"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                  {formatDuration(video.duration_seconds)}
                </div>
              </div>
              <div className="p-3">
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">{video.prompt}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Video className="w-3 h-3" />
                    {video.aspect_ratio}
                  </span>
                  <span>{new Date(video.created_at).toLocaleDateString('ja-JP')}</span>
                </div>
                  {historyView === 'organization' && video.user && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                      <User className="w-3 h-3" />
                      <span>{video.user.name || video.user.email}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}


      {/* Loading Indicator */}
      {currentLoading && <Loading text="読み込み中..." />}

      {/* End of Content Indicator */}
      {!currentHasMore && currentContent.length > 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">すべての動画を表示しました</p>
        </div>
      )}

      {/* Video Detail Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex">
            <div className="flex-1 flex items-center justify-center bg-black">
              <video
                src={selectedVideo.url}
                controls
                autoPlay
                className="max-w-full max-h-full"
                style={{
                  aspectRatio: selectedVideo.aspect_ratio === '9:16' ? '9/16' : selectedVideo.aspect_ratio === '1:1' ? '1/1' : '16/9',
                }}
              >
                お使いのブラウザは動画再生をサポートしていません。
              </video>
            </div>
            <div className="w-80 p-6 overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">動画の詳細</h3>
                <button
                  onClick={() => setSelectedVideo(null)}
                  className="h-8 w-8 p-0 hover:bg-gray-100 rounded-md flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">プロンプト</label>
                  <p className="text-sm text-gray-600 mt-1">{selectedVideo.prompt}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">アスペクト比</label>
                  <p className="text-sm text-gray-600 mt-1">{selectedVideo.aspect_ratio}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">再生時間</label>
                  <p className="text-sm text-gray-600 mt-1">{formatDuration(selectedVideo.duration_seconds)}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">ファイルサイズ</label>
                  <p className="text-sm text-gray-600 mt-1">{formatFileSize(selectedVideo.file_size)}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">生成日時</label>
                  <p className="text-sm text-gray-600 mt-1">
                    {new Date(selectedVideo.created_at).toLocaleString('ja-JP')}
                  </p>
                </div>

                <div className="space-y-2 pt-4">
                  <button
                    onClick={() => {
                      setPrompt(selectedVideo.prompt);
                      // 商品モードのままだと履歴のプロンプトが送信文に使われない
                      setInputMode('freetext');
                      setSelectedVideo(null);
                    }}
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white py-2 px-4 rounded-md transition-colors"
                  >
                    同じプロンプトで生成
                  </button>
                  <button
                    onClick={() => handleDownloadVideo(selectedVideo.url, selectedVideo.prompt)}
                    className="w-full border border-gray-300 hover:bg-gray-50 py-2 px-4 rounded-md transition-colors"
                  >
                    ダウンロード
                  </button>
                  <button
                    onClick={() => {
                      handleDeleteVideo(selectedVideo.id);
                      setSelectedVideo(null);
                    }}
                    className="w-full border border-red-300 text-red-600 hover:bg-red-50 py-2 px-4 rounded-md transition-colors"
                  >
                    削除
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Image Selection Modal */}
      <ProductImageSelectionModal
        isOpen={showImageSelectionModal}
        onClose={() => setShowImageSelectionModal(false)}
        onSelect={handleReferenceImagesSelect}
        maxSelection={MAX_REFERENCE_IMAGES}
      />

      {/* Product Info Copy Modal */}
      <ProductInfoCopyModal
        isOpen={showProductInfoModal}
        onClose={() => setShowProductInfoModal(false)}
        onCopyToPrompt={handleCopyToPrompt}
      />

      {showProductSelectionModal && (
        <ProductSelectionModal
          isOpen={showProductSelectionModal}
          onClose={() => setShowProductSelectionModal(false)}
          onConfirm={handleProductSelected}
        />
      )}

      {/* Video Style Select Modal */}
      <VideoStyleSelectModal
        isOpen={showStyleModal}
        onClose={() => setShowStyleModal(false)}
        onSelect={handleStyleSelect}
        currentStyleId={selectedTemplateId}
        templates={adTemplates}
      />

      {/* Video Model Select Modal */}
      <VideoModelSelectModal
        isOpen={showModelModal}
        currentModel={videoModel}
        onSelect={handleModelSelect}
        onClose={() => setShowModelModal(false)}
      />

      {/* Deep-linked Video Job Detail Modal (opened via ?job=<uuid>) */}
      {deepLinkedJobId && (
        <VideoJobDetailModal
          jobId={deepLinkedJobId}
          isOpen={true}
          onClose={() => setDeepLinkedJobId(null)}
          onResubmit={handleResubmitJob}
        />
      )}
    </TaskyPageContainer>
  );
};

export default VideoGenerationTab;
